import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, UserPlus } from 'lucide-react';
import {
  useCountries,
  useDepartments,
  usePositions,
  useContractTypes,
  useCreateEmployee,
  useUpdateEmployee,
  useEmployee,
} from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { PageTransition, FadeIn } from '@/components/ui/motion';

const STEPS = ['Datos Generales', 'Contacto', 'Perfil del Puesto'];

// Etiquetas amigables (en vez de los nombres técnicos como "positionId").
const FIELD_LABELS: Record<string, string> = {
  employeeNumber: 'Número de colaborador',
  firstName: 'Nombres',
  lastName: 'Apellidos',
  identification: 'Identificación (DPI/cédula)',
  birthDate: 'Fecha de nacimiento',
  hireDate: 'Fecha de ingreso',
  countryId: 'País',
  departmentId: 'Departamento',
  positionId: 'Puesto',
  contractTypeId: 'Tipo de contrato',
  corporateEmail: 'Correo corporativo',
  personalEmail: 'Correo personal',
  phone: 'Teléfono',
};

// En qué paso (0/1/2) vive cada campo, para saltar al paso del error.
const STEP_OF_FIELD: Record<string, number> = {
  employeeNumber: 0, firstName: 0, lastName: 0, identification: 0, birthDate: 0,
  hireDate: 0, countryId: 0, departmentId: 0, positionId: 0, contractTypeId: 0,
  corporateEmail: 1, personalEmail: 1, phone: 1,
};

// Campos obligatorios para crear un colaborador.
const REQUIRED_FIELDS = [
  'employeeNumber', 'firstName', 'lastName', 'identification', 'corporateEmail',
  'hireDate', 'countryId', 'departmentId', 'positionId', 'contractTypeId',
];

// Mensaje claro según el tipo de campo.
function friendlyError(field: string): string {
  if (field === 'corporateEmail' || field === 'personalEmail')
    return 'Ingresa un correo válido (ej: nombre@isoft-ste.com).';
  if (field === 'hireDate' || field === 'birthDate') return 'Selecciona una fecha válida.';
  if (['countryId', 'departmentId', 'positionId', 'contractTypeId'].includes(field))
    return 'Debes elegir una opción.';
  return 'Este campo es obligatorio.';
}

export function EmployeeFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { data: existing } = useEmployee(id);
  const { data: countries } = useCountries();
  const { data: departments } = useDepartments();
  const { data: positions } = usePositions();
  const create = useCreateEmployee();
  const update = useUpdateEmployee();
  const [step, setStep] = useState(0);

  const [form, setForm] = useState<any>(
    existing ?? {
      employeeNumber: '',
      firstName: '',
      lastName: '',
      identification: '',
      identificationType: 'DPI',
      corporateEmail: '',
      hireDate: new Date().toISOString().slice(0, 10),
      countryId: '',
      positionId: '',
      departmentId: '',
      contractTypeId: '',
      workMode: 'PRESENCIAL',
    },
  );
  const { data: contractTypes } = useContractTypes(form.countryId);
  const [error, setError] = useState<string | null>(null);
  // Errores por campo (para marcar el campo y mostrar el mensaje debajo).
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const set = (k: string, v: any) =>
    setForm((f: any) => {
      // Al editar un campo, limpiamos su error.
      setFieldErrors((fe) => {
        if (!fe[k]) return fe;
        const next = { ...fe };
        delete next[k];
        return next;
      });
      return { ...f, [k]: v };
    });

  // Marca los campos con error, salta al primer paso que los contiene y
  // muestra un resumen amigable.
  const flagErrors = (fields: string[]) => {
    const fe: Record<string, string> = {};
    fields.forEach((k) => (fe[k] = friendlyError(k)));
    setFieldErrors(fe);
    const firstStep = Math.min(...fields.map((k) => STEP_OF_FIELD[k] ?? 0));
    setStep(firstStep);
    const labels = fields.map((k) => FIELD_LABELS[k] ?? k);
    setError(
      `Revisa estos datos antes de continuar: ${labels.join(', ')}. Los marcamos en rojo abajo.`,
    );
  };

  const submit = async () => {
    setError(null);
    setFieldErrors({});
    // Los campos opcionales enviados como "" rompen los validadores del backend
    // (Zod .optional() solo acepta undefined). Limpiamos los vacíos.
    const cleaned: any = {};
    for (const [k, v] of Object.entries(form)) {
      cleaned[k] = typeof v === 'string' && v.trim() === '' ? undefined : v;
    }

    // Validación de obligatorios (cliente).
    const missing = REQUIRED_FIELDS.filter((k) => !cleaned[k]);
    if (missing.length) {
      flagErrors(missing);
      return;
    }

    try {
      if (isEdit) await update.mutateAsync({ id: id!, payload: cleaned });
      else await create.mutateAsync(cleaned);
      navigate('/colaboradores');
    } catch (err: any) {
      // Si el backend devolvió errores por campo (Zod), los traducimos a labels.
      const zodFields = err.response?.data?.error?.details?.issues?.fieldErrors;
      const bad = zodFields
        ? Object.keys(zodFields).filter((k) => zodFields[k]?.length)
        : [];
      if (bad.length) {
        flagErrors(bad);
        return;
      }
      setError(
        err.response?.data?.error?.message ??
          'No se pudo guardar. Revisa tu conexión e intenta de nuevo.',
      );
    }
  };

  return (
    <PageTransition className="flex flex-col gap-6">
      <Link
        to="/colaboradores"
        className="inline-flex items-center gap-2 text-sm font-medium text-font-secondary transition-colors hover:text-primary"
      >
        <ArrowLeft size={16} /> Volver
      </Link>

      {/* Header con acento de marca */}
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
          <UserPlus size={22} className="text-primary" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-font-primary">
            {isEdit ? 'Editar colaborador' : 'Nuevo colaborador'}
          </h1>
          <p className="mt-0.5 text-sm text-font-secondary">
            Completa los datos en cada paso para registrar al colaborador
          </p>
        </div>
      </div>

      {/* Step breadcrumb */}
      <FadeIn delay={0.05}>
        <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            {STEPS.map((s, i) => {
              const isDone = i < step;
              const isCurrent = i === step;
              return (
                <div key={s} className="flex items-center gap-2">
                  <button
                    onClick={() => setStep(i)}
                    className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold transition ${
                      isCurrent
                        ? 'bg-primary-subtle text-primary'
                        : isDone
                        ? 'text-accent'
                        : 'text-font-tertiary'
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] transition ${
                        isCurrent
                          ? 'bg-primary text-white'
                          : isDone
                          ? 'bg-accent text-white'
                          : 'bg-border text-font-secondary'
                      }`}
                    >
                      {isDone ? <Check size={12} /> : i + 1}
                    </span>
                    {s}
                  </button>
                  {i < STEPS.length - 1 && (
                    <ChevronRight size={14} className="text-font-tertiary" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={0.1}>
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
        {step === 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="# Colaborador" required error={fieldErrors.employeeNumber}>
              <input
                className="input h-10"
                value={form.employeeNumber}
                onChange={(e) => set('employeeNumber', e.target.value)}
              />
            </Field>
            <Field label="Identificación (DPI/cédula)" required error={fieldErrors.identification}>
              <input
                className="input h-10"
                value={form.identification}
                onChange={(e) => set('identification', e.target.value)}
              />
            </Field>
            <Field label="Nombres" required error={fieldErrors.firstName}>
              <input
                className="input h-10"
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
              />
            </Field>
            <Field label="Apellidos" required error={fieldErrors.lastName}>
              <input
                className="input h-10"
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
              />
            </Field>
            <Field label="Fecha de nacimiento">
              <input
                type="date"
                className="input h-10"
                value={form.birthDate ?? ''}
                onChange={(e) => set('birthDate', e.target.value)}
              />
            </Field>
            <Field label="Fecha de ingreso" required error={fieldErrors.hireDate}>
              <input
                type="date"
                className="input h-10"
                value={form.hireDate}
                onChange={(e) => set('hireDate', e.target.value)}
              />
            </Field>
            <Field label="País" required error={fieldErrors.countryId}>
              <select
                className="input h-10"
                value={form.countryId}
                onChange={(e) => set('countryId', e.target.value)}
              >
                <option value="">Selecciona un país</option>
                {countries?.items?.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.nameEs}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Departamento" required error={fieldErrors.departmentId}>
              <select
                className="input h-10"
                value={form.departmentId}
                onChange={(e) => set('departmentId', e.target.value)}
              >
                <option value="">Selecciona departamento</option>
                {departments?.items?.map((d: any) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Puesto" required error={fieldErrors.positionId}>
              <select
                className="input h-10"
                value={form.positionId}
                onChange={(e) => set('positionId', e.target.value)}
              >
                <option value="">Selecciona puesto</option>
                {positions?.items?.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Tipo de contrato" required error={fieldErrors.contractTypeId}>
              <select
                className="input h-10"
                value={form.contractTypeId}
                onChange={(e) => set('contractTypeId', e.target.value)}
              >
                <option value="">Selecciona contrato</option>
                {contractTypes?.items?.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Correo corporativo" required error={fieldErrors.corporateEmail}>
              <input
                type="email"
                className="input h-10"
                value={form.corporateEmail}
                onChange={(e) => set('corporateEmail', e.target.value)}
              />
            </Field>
            <Field label="Correo personal">
              <input
                type="email"
                className="input h-10"
                value={form.personalEmail ?? ''}
                onChange={(e) => set('personalEmail', e.target.value)}
              />
            </Field>
            <Field label="Teléfono">
              <input
                className="input h-10"
                value={form.phone ?? ''}
                onChange={(e) => set('phone', e.target.value)}
              />
            </Field>
            <Field label="Dirección">
              <input
                className="input h-10"
                value={form.address ?? ''}
                onChange={(e) => set('address', e.target.value)}
              />
            </Field>
            <Field label="Contacto de emergencia">
              <input
                className="input h-10"
                value={form.emergencyContact ?? ''}
                onChange={(e) => set('emergencyContact', e.target.value)}
              />
            </Field>
            <Field label="Teléfono de emergencia">
              <input
                className="input h-10"
                value={form.emergencyPhone ?? ''}
                onChange={(e) => set('emergencyPhone', e.target.value)}
              />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-4">
            <Field label="Función principal">
              <textarea
                rows={3}
                className="input"
                value={form.mainFunction ?? ''}
                onChange={(e) => set('mainFunction', e.target.value)}
              />
            </Field>
            {[1, 2, 3].map((n) => (
              <Field key={n} label={`Responsabilidad ${n}`}>
                <input
                  className="input h-10"
                  value={form[`responsibility${n}`] ?? ''}
                  onChange={(e) => set(`responsibility${n}`, e.target.value)}
                />
              </Field>
            ))}
          </div>
        )}

          {step === STEPS.length - 1 && (
            <p className="mt-6 rounded-md border border-dashed border-border bg-bg p-4 text-xs text-font-secondary">
              Tras crear al colaborador podrás cargar sus documentos, evaluaciones y
              datos de cumplimiento desde la vista de detalle.
            </p>
          )}

          {error && <p className="mt-4 text-sm text-error">{error}</p>}

          <div className="mt-8 flex justify-between border-t border-border pt-5">
            <Button
              type="button"
              variant="outline"
              onClick={() => (step > 0 ? setStep(step - 1) : navigate('/colaboradores'))}
            >
              {step === 0 ? 'Cancelar' : 'Anterior'}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button type="button" variant="default" onClick={() => setStep(step + 1)}>
                Siguiente <ChevronRight size={14} />
              </Button>
            ) : (
              <Button
                variant="gradient"
                onClick={submit}
                disabled={create.isPending || update.isPending}
              >
                {isEdit ? 'Guardar cambios' : 'Crear colaborador'}
              </Button>
            )}
          </div>
        </div>
      </FadeIn>
    </PageTransition>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span
        className={`text-xs font-semibold ${error ? 'text-error' : 'text-font-primary'}`}
      >
        {label} {required && <span className="text-error">*</span>}
      </span>
      <div className={error ? 'rounded-md ring-2 ring-error/50' : ''}>{children}</div>
      {error && <span className="text-xs font-medium text-error">{error}</span>}
    </label>
  );
}
